<template>
    <main class="flex justify-center bg-gray-50">
      <HeaderComponent />
      <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">
        <!-- Notifications -->
        <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
            <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ successMessage }}
            </div>
        </div>

        <!-- Loader -->
        <div v-if="isLoading" class="fixed inset-0 z-50 lg:left-[280px] flex items-center justify-center bg-black bg-opacity-30">
            <div class="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                <svg class="animate-spin h-8 w-8 text-orange-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="text-gray-700">Chargement en cours...</p>
            </div>
        </div>

        <!-- Transfer section -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 class="text-xl font-bold text-gray-800 mb-4">Transférer un Produit</h2>
            
            <!-- Search input -->
            <div class="relative mb-4">
                <input
                    v-model="query"
                    @input="searchProduits(query)"
                    type="text"
                    placeholder="Rechercher un produit par IMEI ou nom..."
                    class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                
                <!-- Suggestions dropdown -->
                <ul v-if="suggestions.length" class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <li
                        v-for="produit in suggestions"
                        :key="produit.produitId"
                        @click="selectProduit(produit)"
                        class="p-3 cursor-pointer hover:bg-orange-50 border-b border-gray-100 last:border-b-0"
                    >
                        <div class="flex justify-between items-center">
                            <div>
                                <p class="font-medium text-gray-800">{{ produit.lotName }}</p>
                                <p class="text-xs text-gray-500">{{ produit.imei1 }}</p>
                            </div>
                            <span class="text-sm font-semibold text-gray-700">{{ produit.stock }}</span>
                        </div>
                    </li>
                </ul>
            </div>
          
            <!-- Selected product details -->
            <div v-if="selectedProduit" class="mt-6 p-4 bg-gray-50 rounded-lg">
                <div class="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <p class="text-sm font-medium text-gray-600">Produit</p>
                        <p class="font-semibold">{{ selectedProduit.lotName }}</p>
                    </div>
                    <div>
                        <p class="text-sm font-medium text-gray-600">IMEI</p>
                        <p class="font-semibold">{{ selectedProduit.imei1 || 'Accessoire' }}</p>
                    </div>
                    <div>
                        <p class="text-sm font-medium text-gray-600">Stock actuel</p>
                        <p class="font-semibold">{{ selectedProduit.quantity || selectedProduit.stock }}</p>
                    </div>
                    <div>
                        <p class="text-sm font-medium text-gray-600">Catégorie</p>
                        <p class="font-semibold">{{ selectedProduit.lotCategorie }}</p>
                    </div>
                </div>
                
                <!-- Quantité à transférer (pour accessoires) -->
                <div v-if="!selectedProduit.imei1" class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Quantité à transférer</label>
                    <input
                        v-model.number="transferQuantity"
                        type="number"
                        min="1"
                        :max="selectedProduit.quantity"
                        placeholder="Entrez la quantité"
                        class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        @input="validateQuantity"
                    />
                    <p v-if="quantityError" class="text-red-500 text-xs mt-1">{{ quantityError }}</p>
                </div>
                
                <!-- Destination selection -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Destination du transfert</label>
                    <select 
                        v-model="newStock" 
                        required 
                        class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                        <option value="" disabled selected>Sélectionnez une destination</option>
                        <option value="Stock Parakou">Stock Parakou</option>
                        <option value="Stock Calavi">Stock Calavi</option>
                        <option value="Magasin Parakou">Magasin Parakou</option>
                        <option value="Magasin Calavi">Magasin Calavi</option>
                    </select>
                </div>
                
                <button
                    @click="sendTransfere()"
                    :disabled="isPending"
                    class="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg font-medium flex justify-center items-center"
                >
                    <span v-if="!isPending">Effectuer le transfert</span>
                    <span v-if="isPending" class="flex items-center">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        En cours...
                    </span>
                </button>
            </div>
        </div>

        <!-- Transfer list -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div class="flex justify-between items-center mb-4">
                <h1 class="text-lg font-bold text-gray-800">Historique des Transferts</h1>
                <span class="text-xs bg-gray-100 px-2 py-1 rounded-full font-medium">
                    {{ pagination.totalTransferes }} transfert(s)
                </span>
            </div>

            <!-- Tableau compact pour les transferts -->
            <div class="overflow-x-auto hidden md:flex">
                <table v-if="transferes && transferes.length !== 0" class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-3 py-2 text-left font-medium text-gray-500">Produit</th>
                            <th class="px-3 py-2 text-left font-medium text-gray-500 hidden sm:table-cell">Transfert</th>
                            <th class="px-3 py-2 text-left font-medium text-gray-500">Quantité</th>
                            <th class="px-3 py-2 text-left font-medium text-gray-500">Statut</th>
                            <th class="px-3 py-2 text-left font-medium text-gray-500 hidden md:table-cell">Par</th>
                            <th class="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                            <th v-if="user.isSuper" class="px-3 py-2 text-right font-medium text-gray-500">Action</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                        <tr 
                            v-for="(transfere, index) in transferes" 
                            :key="index" 
                            class="hover:bg-gray-50 transition-colors my-[5px]"
                            :class="{'bg-green-200': transfere.isValide, 'bg-orange-200': !transfere.isValide}"
                        >
                            <!-- Nom du produit -->
                            <td class="px-3 py-2 whitespace-nowrap font-medium text-gray-800">
                                <div class="flex items-center">
                                    <span class="truncate max-w-[120px]">{{ transfere.nom }}</span>
                                </div>
                            </td>

                            <!-- Détails du transfert -->
                            <td class="px-3 py-2 whitespace-nowrap hidden sm:table-cell">
                                <div class="flex items-center space-x-1">
                                    <span class="text-gray-600">{{ transfere.stockDepart }}</span>
                                    <i class="fas fa-arrow-right text-xs text-gray-400"></i>
                                    <span class="text-gray-800 font-medium">{{ transfere.stockFinale }}</span>
                                </div>
                            </td>

                            <!-- Quantité -->
                            <td class="px-3 py-2 whitespace-nowrap text-center">
                                <span class="inline-flex items-center justify-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-semibold">
                                    {{ transfere.quantity || 1 }}
                                </span>
                            </td>

                            <!-- Statut -->
                            <td class="px-3 py-2 whitespace-nowrap">
                                <span 
                                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                    :class="{'bg-green-100 text-green-800': transfere.isValide, 'bg-orange-100 text-orange-800': !transfere.isValide}"
                                >
                                    {{ transfere.isValide ? 'Validé' : 'En attente' }}
                                </span>
                            </td>

                            <!-- Vendeur -->
                            <td class="px-3 py-2 whitespace-nowrap hidden md:table-cell text-gray-600">
                                {{ transfere.firstname || 'Prénoms' }} {{ transfere.lastname || 'Nom' }}
                            </td>

                            <!-- Date -->
                            <td class="px-3 py-2 whitespace-nowrap text-gray-500">
                                {{ new Date(transfere.createdAt).toLocaleDateString() }}
                            </td>

                            <!-- Action admin -->
                            <td v-if="user.isSuper" class="px-3 py-2 whitespace-nowrap text-right">
                                <button 
                                    @click="transfere.isValide ? retourMiseAJourStock(transfere) : initierMiseAJourStock(transfere)"
                                    class="p-1 rounded-full"
                                    :class="{'text-green-600 hover:bg-green-100': transfere.isValide, 'text-orange-600 hover:bg-orange-100': !transfere.isValide}"
                                    :title="transfere.isValide ? 'Annuler le transfert' : 'Valider le transfert'"
                                >
                                    <i class="fas fa-sync-alt text-sm"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- État vide -->
                <div v-if="transferes.length === 0" class="text-center flex items-center justify-center flex-col w-full py-8">
                    <i class="fas fa-exchange-alt text-3xl text-gray-300 mb-2"></i>
                    <p class="text-gray-500 text-sm">Aucun transfert enregistré</p>
                </div>
            </div>

            <!-- Version mobile compacte -->
            <div v-if="transferes && transferes.length !== 0" class="md:hidden space-y-2 mt-2">
                <div 
                    v-for="(transfere, index) in transferes" 
                    :key="'mobile-'+index" 
                    class="p-3 rounded-lg border border-gray-200"
                    :class="{'bg-green-50': transfere.isValide, 'bg-orange-50': !transfere.isValide}"
                >
                    <div class="flex justify-between items-start">
                        <div class="flex-1">
                            <h3 class="font-medium text-gray-800 truncate">{{ transfere.nom }}</h3>
                            <div class="flex items-center mt-1 text-xs text-gray-600">
                                <span>{{ transfere.stockDepart }}</span>
                                <i class="fas fa-arrow-right mx-1 text-xs"></i>
                                <span class="font-medium">{{ transfere.stockFinale }}</span>
                                <span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-800 text-xs font-semibold">
                                    x{{ transfere.quantity || 1 }}
                                </span>
                            </div>
                        </div>
                        <span 
                            class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            :class="{'bg-green-100 text-green-800': transfere.isValide, 'bg-orange-100 text-orange-800': !transfere.isValide}"
                        >
                            {{ transfere.isValide ? 'Validé' : 'En attente' }}
                        </span>
                    </div>
                    <div class="flex justify-between items-center mt-2 text-xs text-gray-500">
                        <span>{{ new Date(transfere.createdAt).toLocaleDateString() }} | {{ transfere.firstname || 'Prénoms' }} {{ transfere.lastname || 'Nom' }}</span>
                        
                        <button 
                            v-if="user.isSuper"
                            @click="transfere.isValide ? retourMiseAJourStock(transfere) : initierMiseAJourStock(transfere)"
                            class="p-1"
                            :title="transfere.isValide ? 'Annuler' : 'Valider'"
                        >
                            <i class="fas fa-sync-alt text-sm"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="pagination.totalPages > 1" class="flex items-center justify-center mt-6 gap-1">
                <!-- Bouton Précédent -->
                <button 
                    @click="changePage(pagination.currentPage - 1)"
                    :disabled="pagination.currentPage === 1"
                    class="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Page précédente"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
              
                <!-- Pages -->
                <template v-for="(page, index) in visiblePages">
                    <button
                        v-if="page !== '...'"
                        :key="'page-' + index"
                        @click="changePage(page)"
                        class="w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors"
                        :class="{
                            'bg-orange-500 text-white hover:bg-orange-600': pagination.currentPage === page,
                            'text-gray-700 hover:bg-gray-100': pagination.currentPage !== page
                        }"
                    >
                        {{ page }}
                    </button>
                    <span 
                        v-else
                        :key="'ellipsis-' + index"
                        class="w-10 h-10 flex items-center justify-center text-gray-500"
                    >
                        {{ page }}
                    </span>
                </template>
              
                <!-- Bouton Suivant -->
                <button 
                    @click="changePage(pagination.currentPage + 1)"
                    :disabled="pagination.currentPage === pagination.totalPages"
                    class="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Page suivante"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
      </section>
    </main>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import HeaderComponent from '../components/HeaderComponent.vue';
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const query = ref('');
const suggestions = ref([]);
const selectedProduit = ref(null);
const newStock = ref('');
const isPending = ref(false);
const transferQuantity = ref(1);
const quantityError = ref('');
const produits = ref([]);
const nom = ref('')
const stockFinale = ref('')
const stockDepart = ref('')
const errorMessage = ref('')
const successMessage = ref('')
const isLoading = ref(false); 

// Pagination
const pagination = ref({
    currentPage: 1,
    totalPages: 1,
    totalTransferes: 0,
    limit: 20
});

const visiblePages = computed(() => {
    const current = pagination.value.currentPage;
    const total = pagination.value.totalPages;
    const range = 1; // Nombre de pages à afficher de chaque côté de la page actuelle
    
    let pages = [];
    
    // Toujours ajouter la première page
    if (total > 0) pages.push(1);
    
    // Ajouter les pages autour de la page courante
    const start = Math.max(2, current - range);
    const end = Math.min(total - 1, current + range);
    
    if (start > 2) pages.push('...');
    
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
    
    if (end < total - 1) pages.push('...');
    
    // Toujours ajouter la dernière page si différente de la première
    if (total > 1) pages.push(total);
    
    return pages;
});

const fetchProduits = async () => {
  isLoading.value = true; // Activer le loader
  try {
    const response = await axios.get('/api/lots', {
      params: {
        search: '',
        limit: 1000
      }
    }); 
    
    produits.value = response.data.lots.flatMap(lot => 
      lot.produits.map(produit => ({
        imei1: produit.imei1,
        imei2: produit.imei2,
        prix: produit.prix,
        stock: produit.stock,
        produitId: produit._id,
        prix_reduit: produit.prix_reduit,
        quantity: produit.quantite,
        lotName: lot.name,
        lotId: lot._id,
        photos: lot.photos,
        lotCategorie: lot.categorie,
      }))
    );
  } catch (error) {
    console.error('Erreur lors de la récupération des produits :', error);
  } finally {
    isLoading.value = false; // Désactiver le loader
  }
};

const fetchTransfere = async () => {
    isLoading.value = true; // Activer le loader
    try {
        const response = await axios.get('/api/transfere/getAll', {
            params: {
                page: pagination.value.currentPage,
                limit: pagination.value.limit
            }
        });
        transferes.value = response.data.transferes;
        pagination.value = {
            currentPage: response.data.currentPage,
            totalPages: response.data.totalPages,
            totalTransferes: response.data.totalTransferes,
            limit: pagination.value.limit
        };
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des transferts';
        hideErrorMessageAfterDelay();
    } finally {
        isLoading.value = false; // Désactiver le loader
    }
};

const searchProduits = async (query) => {
  if (query.length > 0) {
    try {
      const response = await axios.get('/api/lots', {
        params: {
          search: query,
          limit: 20
        }
      });
      
      suggestions.value = response.data.lots.flatMap(lot => 
        lot.produits.map(produit => ({
          imei1: produit.imei1,
          imei2: produit.imei2,
          prix: produit.prix,
          stock: produit.stock,
          produitId: produit._id,
          prix_reduit: produit.prix_reduit,
          quantity: produit.quantite,
          lotName: lot.name,
          lotId: lot._id,
          photos: lot.photos,
          lotCategorie: lot.categorie,
        }))
      );
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      suggestions.value = [];
    }
  } else {
    suggestions.value = [];
  }
};

const selectProduit = (produit) => {
  selectedProduit.value = produit;
  query.value = produit.lotName; 
  suggestions.value = [];
  // Réinitialiser la quantité à 1 pour chaque nouvelle sélection
  transferQuantity.value = 1;
  quantityError.value = '';
};

const validateQuantity = () => {
  const maxQuantity = selectedProduit.value?.quantity || 0;
  if (transferQuantity.value < 1) {
    quantityError.value = 'La quantité doit être au moins 1';
    transferQuantity.value = 1;
  } else if (transferQuantity.value > maxQuantity) {
    quantityError.value = `La quantité maximale disponible est ${maxQuantity}`;
    transferQuantity.value = maxQuantity;
  } else {
    quantityError.value = '';
  }
};

const sendTransfere = async () => {
    if (!newStock.value) {
      errorMessage.value = 'Veuillez sélectionner une destination valide';
      hideErrorMessageAfterDelay();
      return;
    }
    
    // Validation pour les accessoires
    if (!selectedProduit.value.imei1) {
      if (!transferQuantity.value || transferQuantity.value < 1) {
        errorMessage.value = 'Veuillez entrer une quantité valide';
        hideErrorMessageAfterDelay();
        return;
      }
      if (transferQuantity.value > selectedProduit.value.quantity) {
        errorMessage.value = 'La quantité demandée dépasse le stock disponible';
        hideErrorMessageAfterDelay();
        return;
      }
    }
    
    isPending.value = true;
    try {
        const response = await axios.post('/api/transfere/send', 
        {
            nom: selectedProduit.value.lotName,
            idProduit: selectedProduit.value.produitId,
            firstname: user.value.firstname,
            lastname: user.value.lastname,
            stockDepart: selectedProduit.value.stock,
            stockFinale: newStock.value,
            quantity: selectedProduit.value.imei1 ? 1 : transferQuantity.value // Quantité 1 pour téléphones, variable pour accessoires
        });
        if (response.status === 201 || response.status == 200) {
            successMessage.value = 'Transfert enregistré avec succès!';
            hideSuccessMessageAfterDelay();
        }
        query.value = '';
        selectedProduit.value = null;
        newStock.value = '';
        transferQuantity.value = 1;
        quantityError.value = '';
        stockDepart.value = ""
        stockFinale.value = ""
        fetchTransfere()
    } catch (error) {
        errorMessage.value = 'Erreur lors de l\'enregistrement du transfert';
        hideErrorMessageAfterDelay()
    } finally {
        isPending.value = false;
    }
};

const toggleActive = async (id) => {
  try {
    await axios.put(`/api/transfere/${id}/toggle-active/`);
    const index = transferes.value.findIndex((e) => e._id === id);
    if (index !== -1) {
      transferes.value[index].isValide = true;
    }
  } catch (error) {
    errorMessage.value = 'Erreur lors du changement de statut';
    hideErrorMessageAfterDelay();
  }
};

const initierMiseAJourStock = async (transfere) => {
  isPending.value = true; 
  try {
    const stockResponse = await axios.put(`/api/produits/${transfere.idProduit}/update-stock`, {
      newStock: transfere.stockFinale,
      quantity: transfere.quantity || 1, // Envoyer la quantité du transfert
    });

    if (stockResponse.status === 200 || stockResponse.status === 201) {
      await toggleActive(transfere._id);
    }

    await fetchTransfere();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la mise à jour du stock';
    hideErrorMessageAfterDelay()
  } finally {
    isPending.value = false; 
  }
};

const retourMiseAJourStock = async (transfere) => {
  isPending.value = true; 
  try {
    // Pour annuler un transfert, on transfère la quantité dans le sens inverse
    const stockResponse = await axios.put(`/api/produits/${transfere.idProduit}/update-stock`, {
      newStock: transfere.stockDepart,
      quantity: transfere.quantity || 1, // Envoyer la quantité du transfert
    });

    if (stockResponse.status === 200 || stockResponse.status === 201) {
      await toggleActive(transfere._id);
    }

    await fetchTransfere();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la mise à jour du stock';
    hideErrorMessageAfterDelay()
  } finally {
    isPending.value = false; 
  }
};

const transferes = ref([])

const changePage = (page) => {
    if (page >= 1 && page <= pagination.value.totalPages) {
        pagination.value.currentPage = page;
        fetchTransfere();
    }
};

const hideSuccessMessageAfterDelay = () => {
    setTimeout(() => {
      successMessage.value = ''; 
    }, 5000);
};
  
const hideErrorMessageAfterDelay = () => {
    setTimeout(() => {
      errorMessage.value = ''; 
    }, 10000);
};

onMounted(() => {
  fetchProduits();
  fetchTransfere()
});
</script>

<style scoped>
/* Custom animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease-out;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>